/**
 * Tabs, restyled from Radix.
 *
 * Radix supplies the roving tabindex and the arrow-key navigation the ARIA
 * tabs pattern requires — behaviour that is invisible until a keyboard user
 * finds it missing.
 *
 * Below 768px the strip scrolls horizontally rather than wrapping: six tabs
 * wrapped onto three rows at 375px push the content below the fold, and
 * plan §7.3 asks for scrollable tabs at that width.
 *
 * The prototype also puts a count pill on tabs that have one (Team, Documents).
 * Not built here: the only count this page has is the exit checklist's, and a
 * number beside "Overview" would be read as the count of something the tab is
 * about. It lands with the entities that supply real counts, in Phase 7.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.5, 6.5.9, §7.3
 */
import * as RadixTabs from '@radix-ui/react-tabs';

import { cn } from '../../lib/cn';

export interface TabDefinition {
  value: string;
  label: string;
}

export interface TabsProps {
  tabs: readonly TabDefinition[];
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name for the tab strip. */
  ariaLabel: string;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({
  tabs,
  value,
  onValueChange,
  ariaLabel,
  children,
  className,
}: TabsProps): React.JSX.Element {
  return (
    <RadixTabs.Root value={value} onValueChange={onValueChange} className={className}>
      <RadixTabs.List
        aria-label={ariaLabel}
        className="border-gray-100 -mx-1 flex gap-0.5 overflow-x-auto border-b px-1"
      >
        {tabs.map((tab) => (
          <RadixTabs.Trigger
            key={tab.value}
            value={tab.value}
            className={cn(
              /* The display font, per the prototype: chrome is Gilroy, not
                 only headings (§4.5). */
              'font-display min-h-11 flex-none px-s3 text-[13px] font-bold whitespace-nowrap',
              'text-body hover:text-navy-text border-b-2 border-transparent transition-colors',
              'data-[state=active]:border-blue data-[state=active]:text-navy-text',
            )}
          >
            {tab.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {children}
    </RadixTabs.Root>
  );
}
